import { minBy } from 'es-toolkit';

import { distanceEquals, Point2D } from '@paragon/geometry-library';

import { EntitySnapTarget, EntitySnapTargetType } from './EntitySnapTarget';

/**
 * Returns the closest target to the given point, or null if there are no suitable targets.
 *
 * @param point
 * @param options
 *          preferredSnapType: The entity snap target types which will be preferred over other targets if they are
 *                               equally close to the given point.
 */
export function getClosestEntitySnapTarget(
  targets: EntitySnapTarget[],
  point: Point2D,
  options?: { preferredSnapType?: EntitySnapTargetType },
): EntitySnapTarget | null {
  const targetsWithDistances = targets.map((target) => ({ target, distance: target.distanceFrom(point) }));

  const closest = getClosest(targetsWithDistances);
  if (closest == null) return null;

  let closestPreferred: { target: EntitySnapTarget; distance: number } | undefined;

  if (options?.preferredSnapType) {
    const filteredTargets = targetsWithDistances.filter(({ target }) => target.type === options.preferredSnapType);
    if (filteredTargets.length > 0) {
      closestPreferred = getClosest(filteredTargets);
    }
  }

  return closestPreferred && distanceEquals(closestPreferred.distance, closest.distance)
    ? closestPreferred.target
    : closest.target;
}

function getClosest(targets: { target: EntitySnapTarget; distance: number }[]) {
  return minBy(targets, ({ distance }) => distance);
}
